import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import { notify } from '../../components/Utils/Notifications';

/**
 * Horaires de travail de la société (contrat C17, Yros/Kavero#16).
 *
 * Le temps attendu d'une journée vient du calendrier de la société : c'est lui
 * qu'Odoo compare au temps pointé pour décider ce qui dépasse. Tant qu'il ne se
 * réglait que dans Odoo, une société qui ne travaille pas de 8 h à 17 h n'avait
 * aucun moyen de le dire.
 *
 * Les sept jours sont toujours affichés, même ceux qui ne sont pas travaillés :
 * c'est la seule façon d'ouvrir le samedi. Décocher un jour ne vide pas ses
 * heures à l'écran — elles restent visibles, grisées, et reviennent si la RH
 * se ravise avant d'enregistrer.
 *
 * Seuls les jours modifiés partent : un jour absent du corps n'est pas touché
 * côté Odoo, donc un enregistrement ne réécrit que ce que la RH a changé.
 */

const NUMEROS = [0, 1, 2, 3, 4, 5, 6];
// Même borne qu'Odoo et que le proxy : au-delà, ce n'est plus une tolérance.
const TOLERANCE_MAX_MINUTES = 120;
const HEURES_PAR_DEFAUT = { debut: '08:00', fin: '17:00', pause_debut: '12:00', pause_fin: '13:00' };
// [champ du contrat, suffixe du testid, clé du libellé court]
const CHAMPS_HEURE = [
  ['debut', 'debut', 'debut'],
  ['fin', 'fin', 'fin'],
  ['pause_debut', 'pauseDebut', 'pauseDebutCourt'],
  ['pause_fin', 'pauseFin', 'pauseFinCourt'],
];

const CLASSE_HEURE = 'mt-1 w-full px-2 py-1.5 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm disabled:opacity-40';
const CLASSE_ERREUR = 'text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2';

// Un champ `time` vide rend '' : il ne doit pas coercer vers 0 heure.
const enMinutes = (heure) => {
  const parties = /^(\d{2}):(\d{2})$/.exec(String(heure ?? ''));
  return parties ? Number(parties[1]) * 60 + Number(parties[2]) : NaN;
};

const normaliser = (horaires) => ({
  jours: NUMEROS.map((numero) => {
    const recu = (horaires?.jours || []).find((jour) => jour.jour === numero) || {};
    return {
      jour: numero,
      travaille: Boolean(recu.travaille),
      debut: recu.debut || '',
      fin: recu.fin || '',
      pause_debut: recu.pause_debut || '',
      pause_fin: recu.pause_fin || '',
    };
  }),
  toleranceSociete: String(horaires?.tolerance_societe_minutes ?? 0),
  toleranceSalarie: String(horaires?.tolerance_salarie_minutes ?? 0),
  comptageActif: Boolean(horaires?.comptage_actif),
  comptageDepuis: horaires?.comptage_depuis || '',
  feuilleSource: horaires?.feuille_source === 'horaire' ? 'horaire' : 'pointage',
  // Yros/Kavero#491 : qui valide les heures sup du pointage ; à défaut, la RH.
  validateur: horaires?.validateur_heures_sup
    ? { value: horaires.validateur_heures_sup.employe_id, label: horaires.validateur_heures_sup.nom }
    : null,
});

const jourValide = (jour) => {
  if (!jour.travaille) return true;
  const debut = enMinutes(jour.debut);
  const fin = enMinutes(jour.fin);
  if (!Number.isFinite(debut) || !Number.isFinite(fin) || debut >= fin) return false;

  const pauseDebut = jour.pause_debut ? enMinutes(jour.pause_debut) : null;
  const pauseFin = jour.pause_fin ? enMinutes(jour.pause_fin) : null;
  if (pauseDebut === null && pauseFin === null) return true;
  // Une pause à moitié remplie n'est pas une pause : Odoo la refuserait, autant
  // le dire ici.
  if (pauseDebut === null || pauseFin === null) return false;
  return debut < pauseDebut && pauseDebut < pauseFin && pauseFin < fin;
};

const toleranceValide = (valeur) => /^\d+$/.test(String(valeur).trim())
  && Number(valeur) <= TOLERANCE_MAX_MINUTES;

const dureeDuJour = (jour) => {
  if (!jour.travaille || !jourValide(jour)) return 0;
  const pause = jour.pause_debut && jour.pause_fin
    ? enMinutes(jour.pause_fin) - enMinutes(jour.pause_debut)
    : 0;
  return (enMinutes(jour.fin) - enMinutes(jour.debut) - pause) / 60;
};

// Liste blanche explicite : les heures d'un jour décoché ne partent pas, elles
// n'ont plus de sens ; une pause vide ne part pas non plus — Odoo lit
// l'absence de clé comme « pas de pause ».
const aEnvoyer = (jour) => {
  if (!jour.travaille) return { jour: jour.jour, travaille: false };
  return {
    jour: jour.jour,
    travaille: true,
    debut: jour.debut,
    fin: jour.fin,
    ...(jour.pause_debut && jour.pause_fin
      ? { pause_debut: jour.pause_debut, pause_fin: jour.pause_fin }
      : {}),
  };
};

const memeJour = (gauche, droite) => ['travaille', ...CHAMPS_HEURE.map(([champ]) => champ)]
  .every((champ) => gauche[champ] === droite[champ]);

const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  return enveloppe?.message || t('horairesTravail.erreurTechnique');
};

function HorairesTravail() {
  const { t } = useTranslation();
  const [etat, setEtat] = useState(null);
  const [initial, setInitial] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Yros/Kavero#491 : la société a plusieurs horaires (une équipe en rotation
  // n'a pas celui des bureaux) ; l'écran règle celui qui est choisi.
  const [horaire, setHoraire] = useState(null);
  const [horaires, setHoraires] = useState([]);
  const [nouveauNom, setNouveauNom] = useState(null);

  const reprendre = (donnees) => {
    const normalise = normaliser(donnees);
    setEtat(normalise);
    setInitial(normalise);
    setHoraire(donnees?.horaire || null);
    setHoraires(donnees?.horaires_disponibles || []);
  };

  const charger = useCallback(async (horaireId) => {
    setChargement(true);
    try {
      const reponse = await apiService.lireHorairesTravail(horaireId);
      reprendre(reponse?.horaires);
      setErreur(null);
    } catch (probleme) {
      setErreur(interpreterProbleme(probleme, t));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => { charger(); }, [charger]);

  const creerHoraire = async () => {
    const nom = (nouveauNom || '').trim();
    if (!nom) return;
    setEnCours(true);
    setErreur(null);
    try {
      const reponse = await apiService.ecrireHorairesTravail({ nouvel_horaire: nom });
      reprendre(reponse?.horaires);
      setNouveauNom(null);
      notify(t('horairesTravail.horaireCree'), '', 2);
    } catch (probleme) {
      setErreur(interpreterProbleme(probleme, t));
    } finally {
      setEnCours(false);
    }
  };

  const modifierJour = (numero, champ, valeur) => setEtat((precedent) => ({
    ...precedent,
    jours: precedent.jours.map((jour) => (jour.jour === numero ? { ...jour, [champ]: valeur } : jour)),
  }));

  // Ouvrir un jour sans heure reprend celles du premier jour travaillé : une
  // société qui ouvre le samedi travaille presque toujours aux mêmes heures, et
  // repartir de champs vides ferait ressaisir quatre fois la même chose.
  const basculerJour = (numero) => setEtat((precedent) => {
    const modele = precedent.jours.find((jour) => jour.travaille && jour.debut) || HEURES_PAR_DEFAUT;
    return {
      ...precedent,
      jours: precedent.jours.map((jour) => {
        if (jour.jour !== numero) return jour;
        if (jour.travaille) return { ...jour, travaille: false };
        return {
          ...jour,
          travaille: true,
          debut: jour.debut || modele.debut,
          fin: jour.fin || modele.fin,
          pause_debut: jour.pause_debut || modele.pause_debut || '',
          pause_fin: jour.pause_fin || modele.pause_fin || '',
        };
      }),
    };
  });

  const corps = useMemo(() => {
    if (!etat || !initial) return {};
    const jours = etat.jours
      .filter((jour) => !memeJour(jour, initial.jours.find((autre) => autre.jour === jour.jour)))
      .map(aEnvoyer);
    return {
      ...(jours.length ? { jours } : {}),
      ...(etat.toleranceSociete !== initial.toleranceSociete
        ? { tolerance_societe_minutes: Number(etat.toleranceSociete) } : {}),
      ...(etat.toleranceSalarie !== initial.toleranceSalarie
        ? { tolerance_salarie_minutes: Number(etat.toleranceSalarie) } : {}),
      // L'interrupteur et sa date vont ensemble : changer l'un envoie les deux,
      // puisque c'est le couple qu'Odoo écrit.
      ...(etat.comptageActif !== initial.comptageActif
          || etat.comptageDepuis !== initial.comptageDepuis
        ? {
          comptage_actif: etat.comptageActif,
          ...(etat.comptageActif ? { comptage_depuis: etat.comptageDepuis } : {}),
        } : {}),
      ...(etat.feuilleSource !== initial.feuilleSource ? { feuille_source: etat.feuilleSource } : {}),
      ...((etat.validateur?.value ?? null) !== (initial.validateur?.value ?? null)
        ? { validateur_heures_sup_employe_id: etat.validateur?.value ?? null } : {}),
    };
  }, [etat, initial]);

  const total = useMemo(
    () => (etat ? Math.round(etat.jours.reduce((somme, jour) => somme + dureeDuJour(jour), 0) * 100) / 100 : 0),
    [etat]);

  const enregistrer = async (event) => {
    event.preventDefault();
    if (enCours || !etat) return;

    if (!etat.jours.every(jourValide)) {
      setErreur(t('horairesTravail.heuresInvalides'));
      return;
    }
    if (!toleranceValide(etat.toleranceSociete) || !toleranceValide(etat.toleranceSalarie)) {
      setErreur(t('horairesTravail.toleranceInvalide'));
      return;
    }
    // Odoo combine cette date sans garde dans son calcul : activer le comptage
    // sans elle ferait échouer tout pointage de la société.
    if (etat.comptageActif && !etat.comptageDepuis) {
      setErreur(t('horairesTravail.dateComptageRequise'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      const reponse = await apiService.ecrireHorairesTravail(
        horaire && !horaire.par_defaut ? { ...corps, horaire_id: horaire.id } : corps);
      // Les horaires relus sont la preuve durable de ce qui a été écrit :
      // l'écran les reprend, il ne garde pas la saisie.
      reprendre(reponse?.horaires);
      notify(t('horairesTravail.succes'), '', 2);
    } catch (probleme) {
      setErreur(interpreterProbleme(probleme, t));
    } finally {
      setEnCours(false);
    }
  };

  if (chargement) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
        <h3 className="text-base font-semibold text-slate-800">{t('horairesTravail.titre')}</h3>
        {erreur && <p data-testid="erreurHoraires" className={CLASSE_ERREUR}>{erreur}</p>}
      </div>
    );
  }

  if (!etat) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
        <h3 className="text-base font-semibold text-slate-800">{t('horairesTravail.titre')}</h3>
        <p data-testid="erreurHoraires" className={CLASSE_ERREUR}>{erreur || t('horairesTravail.erreurTechnique')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={enregistrer} className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-slate-800">{t('horairesTravail.titre')}</h3>
        <p className="text-sm text-slate-600">{t('horairesTravail.description')}</p>
      </div>

      {horaires.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm text-slate-700">
            <span className="block mb-1 font-medium">{t('horairesTravail.horaireRegle')}</span>
            <select data-testid="horaires-choix" value={horaire?.id || ''} disabled={enCours}
              onChange={(e) => charger(Number(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
              {horaires.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.par_defaut ? t('horairesTravail.horaireParDefaut', { nom: h.nom }) : h.nom}
                  {` — ${t('horairesTravail.nbSalaries', { count: h.nb_salaries })}`}
                </option>
              ))}
            </select>
          </label>
          {nouveauNom === null ? (
            <button type="button" data-testid="horaires-nouveau" onClick={() => setNouveauNom('')}
              className="px-3 py-2 text-sm rounded-lg border border-[--color-primary] text-[--color-primary] hover:bg-slate-50">
              {t('horairesTravail.nouvelHoraire')}
            </button>
          ) : (
            <span className="flex items-end gap-2">
              <input data-testid="horaires-nouveau-nom" value={nouveauNom} maxLength={128}
                onChange={(e) => setNouveauNom(e.target.value)} placeholder={t('horairesTravail.nomHoraire')}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              <button type="button" data-testid="horaires-creer" onClick={creerHoraire}
                disabled={enCours || !nouveauNom.trim()}
                className="px-3 py-2 text-sm rounded-lg bg-[--color-primary] text-white hover:bg-[--bg-hover] disabled:opacity-50">
                {t('horairesTravail.creer')}
              </button>
              <button type="button" onClick={() => setNouveauNom(null)} className="px-3 py-2 text-sm text-slate-600 hover:underline">
                {t('horairesTravail.annulerCreation')}
              </button>
            </span>
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="hidden sm:grid sm:grid-cols-[minmax(9rem,1fr)_repeat(4,minmax(0,1fr))] gap-2 text-xs font-medium text-slate-500">
          <span>{t('horairesTravail.travaille')}</span>
          <span>{t('horairesTravail.debut')}</span>
          <span>{t('horairesTravail.fin')}</span>
          <span className="sm:col-span-2">{t('horairesTravail.pause')}</span>
        </div>

        {etat.jours.map((jour) => (
          <div
            key={jour.jour}
            className="grid grid-cols-2 sm:grid-cols-[minmax(9rem,1fr)_repeat(4,minmax(0,1fr))] gap-2 items-end border-t border-slate-100 pt-2"
          >
            <label className="col-span-2 sm:col-span-1 flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                data-testid={`jourTravaille-${jour.jour}`}
                checked={jour.travaille}
                onChange={() => basculerJour(jour.jour)}
                className="h-4 w-4 rounded border-slate-300"
              />
              {t(`horairesTravail.jours.${jour.jour}`)}
            </label>

            {CHAMPS_HEURE.map(([champ, suffixe, cleLibelle]) => (
              <label key={champ} className="block text-xs text-slate-500">
                <span className="sm:hidden">{t(`horairesTravail.${cleLibelle}`)}</span>
                <input
                  type="time"
                  data-testid={`${suffixe}-${jour.jour}`}
                  aria-label={`${t(`horairesTravail.jours.${jour.jour}`)} — ${t(`horairesTravail.${cleLibelle}`)}`}
                  value={jour[champ]}
                  disabled={!jour.travaille}
                  onChange={(evenement) => modifierJour(jour.jour, champ, evenement.target.value)}
                  className={CLASSE_HEURE}
                />
              </label>
            ))}
          </div>
        ))}
      </div>

      <p data-testid="totalHebdomadaire" className="text-sm text-slate-600">
        {t('horairesTravail.totalHebdomadaire', { heures: total })}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[['toleranceSociete', 'toleranceSociete'], ['toleranceSalarie', 'toleranceSalarie']].map(([champ, cle]) => (
          <label key={champ} className="block text-sm font-medium text-slate-700">
            {t(`horairesTravail.${cle}`)}
            <input
              type="number"
              min="0"
              max={TOLERANCE_MAX_MINUTES}
              data-testid={champ}
              value={etat[champ]}
              onChange={(evenement) => setEtat((precedent) => ({ ...precedent, [champ]: evenement.target.value }))}
              className={CLASSE_HEURE}
            />
            <span className="block text-xs font-normal text-slate-500">{t(`horairesTravail.${cle}Aide`)}</span>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-slate-100 pt-3">
        <label className="flex items-start gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            data-testid="comptageActif"
            checked={etat.comptageActif}
            onChange={(evenement) => setEtat((precedent) => ({
              ...precedent, comptageActif: evenement.target.checked }))}
            className="mt-1 h-4 w-4 rounded border-slate-300"
          />
          <span>
            {t('horairesTravail.comptageActif')}
            <span className="block text-xs font-normal text-slate-500">
              {t('horairesTravail.comptageActifAide')}
            </span>
          </span>
        </label>
        <label className="block text-sm font-medium text-slate-700">
          {t('horairesTravail.comptageDepuis')}
          <input
            type="date"
            data-testid="comptageDepuis"
            value={etat.comptageDepuis}
            disabled={!etat.comptageActif}
            onChange={(evenement) => setEtat((precedent) => ({
              ...precedent, comptageDepuis: evenement.target.value }))}
            className={CLASSE_HEURE}
          />
        </label>
      </div>

      <fieldset className="border-t border-slate-100 pt-3">
        <legend className="text-sm font-medium text-slate-700">{t('horairesTravail.feuilleSource')}</legend>
        <p className="text-xs text-slate-500">{t('horairesTravail.feuilleSourceAide')}</p>
        <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:gap-6">
          {[['pointage', 'feuilleSourcePointage'], ['horaire', 'feuilleSourceHoraire']].map(([valeur, cle]) => (
            <label key={valeur} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="feuilleSource"
                data-testid={`feuilleSource-${valeur}`}
                checked={etat.feuilleSource === valeur}
                onChange={() => setEtat((precedent) => ({ ...precedent, feuilleSource: valeur }))}
                className="h-4 w-4 border-slate-300"
              />
              {t(`horairesTravail.${cle}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="border-t border-slate-100 pt-3" data-testid="validateurHeuresSup">
        <legend className="text-sm font-medium text-slate-700">{t('horairesTravail.validateurHeuresSup')}</legend>
        <p className="text-xs text-slate-500">{t('horairesTravail.validateurHeuresSupAide')}</p>
        <div className="mt-2 max-w-md">
          <EmployeeSelect
            inputId="validateurHeuresSup"
            value={etat.validateur}
            onChange={(choix) => setEtat((precedent) => ({ ...precedent, validateur: choix || null }))}
            placeholder={t('horairesTravail.validateurHeuresSupDefaut')}
            isDisabled={enCours}
          />
        </div>
      </fieldset>

      {erreur && <p data-testid="erreurHoraires" className={CLASSE_ERREUR}>{erreur}</p>}

      <div className="flex justify-end">
        <button
          type="submit"
          data-testid="enregistrerHoraires"
          disabled={enCours || !Object.keys(corps).length}
          className="px-6 py-2.5 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {enCours ? t('horairesTravail.enregistrementEnCours') : t('horairesTravail.enregistrer')}
        </button>
      </div>
    </form>
  );
}

export default HorairesTravail;
